import React, { useEffect } from 'react';
import { SimpleRouter as BrowserRouter, Routes, Route } from './utils/SimpleRouter';
import axios from 'axios';
import './App.css';
import { getToken } from './utils/storage';
import useTokenExpiry from './hooks/useTokenExpiry';

// Pages
import DisplayScreen from './pages/DisplayScreen';
import ScreensaverPreview from './pages/ScreensaverPreview';
import MobileThankYou from './pages/MobileThankYou';
import SubmitMessage from './pages/SubmitMessage';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/LandingPage';
import OrderForm from './pages/OrderForm';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerScreens from './pages/CustomerScreens';
import CustomerMessages from './pages/CustomerMessages';
import CustomerArticles from './pages/CustomerArticles';
import CustomerImages from './pages/CustomerImages';
import CustomerPosters from './pages/CustomerPosters';
import CustomerUsers from './pages/CustomerUsers';
import CustomerSupport from './pages/CustomerSupport';
import CustomerSettings from './pages/CustomerSettings';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminOrganizations from './pages/SuperAdminOrganizations';
import SuperAdminExport from './pages/SuperAdminExport';
import SuperAdminUsers from './pages/SuperAdminUsers';
import SuperAdminLanguages from './pages/SuperAdminLanguages';
import SuperAdminEmailTemplates from './pages/SuperAdminEmailTemplates';
import SuperAdminSettings from './pages/SuperAdminSettings';
import SuperAdminLoginHistory from './pages/SuperAdminLoginHistory';
import SuperAdminServiceAnnouncements from './pages/SuperAdminServiceAnnouncements';

// Component to use the token expiry hook
function TokenMonitor() {
  useTokenExpiry();
  return null;
}

function App() {
  useEffect(() => {
    // Public API paths that should NOT include authentication tokens
    const PUBLIC_API_PATHS = [
      '/api/display/',
      '/api/public-display/',
      '/api/weather/',
      '/api/submit-message/',
      '/api/public/',
      '/api/auth/login',
      '/api/auth/refresh',
      '/api/service-announcements'
    ];
    
    // Track if we're refreshing to prevent multiple refresh attempts
    let isRefreshing = false;
    let failedQueue = [];
    
    const processQueue = (error, token = null) => {
      failedQueue.forEach(prom => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };
    
    // Setup axios request interceptor to add token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Check if this is a public API request
        const isPublicRequest = PUBLIC_API_PATHS.some(path => config.url?.includes(path));
        
        // Only add token for non-public requests
        if (!isPublicRequest) {
          const token = getToken();
          if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Setup axios response interceptor to handle 401 errors with token refresh
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Skip 401 handling for login and refresh endpoints
        if (originalRequest?.url?.includes('/api/auth/login') || 
            originalRequest?.url?.includes('/api/auth/refresh')) {
          return Promise.reject(error);
        }
        
        // Check if this is an impersonation window
        const isImpersonationWindow = sessionStorage.getItem('isImpersonationWindow') === 'true';
        
        // If 401 error and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          // For impersonation windows, don't try to refresh - just show error
          // Super admin impersonation tokens don't have refresh tokens
          if (isImpersonationWindow) {
            console.log('[TOKEN] Impersonation window - 401 error, closing window');
            alert('Impersoneringsøkten har utløpt. Vinduet vil lukkes.');
            window.close();
            return Promise.reject(error);
          }
          
          const refreshToken = localStorage.getItem('refreshToken');
          
          if (!refreshToken) {
            // No refresh token - redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return Promise.reject(error);
          }
          
          // Queue requests while refreshing
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            }).catch(err => Promise.reject(err));
          }
          
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            console.log('[TOKEN] Attempting to refresh tokens...');
            const refreshResponse = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
              null,
              { params: { refresh_token: refreshToken } }
            );
            
            const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('refreshToken', newRefreshToken);
            
            console.log('[TOKEN] Tokens refreshed successfully');
            processQueue(null, access_token);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return axios(originalRequest);
            
          } catch (refreshError) {
            console.error('[TOKEN] Refresh failed:', refreshError);
            processQueue(refreshError, null);
            
            // Clear auth and redirect
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
            
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <TokenMonitor />
        <Routes>
          {/* Public routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/bestill" element={<OrderForm />} />
          <Route path="/display/:qrCodeId" element={<DisplayScreen />} />
          <Route path="/screensaver-preview" element={<ScreensaverPreview />} />
          <Route path="/mobile/:qrCodeId" element={<MobileThankYou />} />
          <Route path="/submit/:qrCodeId" element={<SubmitMessage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Customer Admin routes */}
          <Route path="/dashboard" element={<CustomerDashboard />} />
          <Route path="/screens" element={<CustomerScreens />} />
          <Route path="/messages" element={<CustomerMessages />} />
          <Route path="/articles" element={<CustomerArticles />} />
          <Route path="/images" element={<CustomerImages />} />
          <Route path="/posters" element={<CustomerPosters />} />
          <Route path="/users" element={<CustomerUsers />} />
          <Route path="/support" element={<CustomerSupport />} />
          <Route path="/settings" element={<CustomerSettings />} />
          
          {/* Super Admin routes */}
          <Route path="/admin/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/admin/organizations" element={<SuperAdminOrganizations />} />
          <Route path="/admin/export" element={<SuperAdminExport />} />
          <Route path="/admin/users" element={<SuperAdminUsers />} />
          <Route path="/admin/languages" element={<SuperAdminLanguages />} />
          <Route path="/admin/email-templates" element={<SuperAdminEmailTemplates />} />
          <Route path="/admin/settings" element={<SuperAdminSettings />} />
          <Route path="/admin/login-history" element={<SuperAdminLoginHistory />} />
          <Route path="/admin/service-announcements" element={<SuperAdminServiceAnnouncements />} />
          
          <Route path="/" element={<Login />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
